import { DestroyRef, inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, filter, switchMap, take, timer } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly _http = inject(HttpClient);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  private _currentVersion: string | null = null;
  private _updateAvailable = false;

  init(intervalMs = 5 * 60 * 1000): void {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return;
    }

    timer(30000, intervalMs)
      .pipe(
        switchMap(() =>
          this._http
            .get<{ version: string }>('/version.json', {
              headers: { hideErrorMessage: '1', hideLoadingSpinner: '1' }
            })
            .pipe(catchError(() => EMPTY))
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((response) => {
        if (this._currentVersion === null) {
          this._currentVersion = response.version;
        } else if (this._currentVersion !== response.version) {
          this._updateAvailable = true;
        }
      });

    // Reload on next navigation if new version detected
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        filter(() => this._updateAvailable),
        take(1),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event: NavigationStart) => {
        window.location.href = event.url;
      });

    // Reload when user returns to tab if new version detected
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this._updateAvailable) {
        window.location.reload();
      }
    });
  }
}
